import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ProgressiveHeroVideo from "../components/ProgressiveHeroVideo";
import NoControlsVideo from "../components/NoControlsVideo";
import { isEmbeddedWebView } from "../utils/inAppBrowser";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  motionValue,
  useAnimationControls,
} from "framer-motion";

/* ─── brand tokens ─── */
const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const INTER = { fontFamily: "'Inter', sans-serif" };
const C = {
  blue: "#0065F8",
  blueLight: "#0A7DFF",
  blueDeep: "#073B8E",
  cyan: "#00CAFF",
  cream: "#F5FAFF",
  cream2: "#EAF3FF",
  navy: "#0B1A33",
  slate: "#334155",
  slate2: "#475569",
  border: "rgba(11,26,51,0.08)",
  shadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
};

const ROTATING_WORDS = ["storybooks", "stories"];

/* transparent-bg clips paired with the rotating word — .mov (HEVC alpha) for
   Safari/iOS, .webm (VP9 alpha) for Chrome/Firefox */
const STORYTELLER_MEDIA = [
  { word: "storybooks", mov: "/assets/books-nobg.mov", webm: "/assets/books-nobg.webm", poster: "/assets/books-nobg-poster.webp", tilt: -2 },
  { word: "stories", mov: "/assets/video-nobg.mov", webm: "/assets/video-nobg.webm", poster: "/assets/video-nobg-poster.webp", tilt: 2 },
];

/* below this width the page switches to its phone layout */
const MOBILE_MAX = 640;

/* ─── "Your world" flying characters ───
   desktop: an ellipse around the title (angle/radius). Phones are too narrow
   for that — the ring lands on the title — so `mobile` places each one
   explicitly: a row above the copy, two staggered rows below (px from the
   section center, laid out for a 390×844 screen and scaled down from there) */
const WORLD_CHARACTERS = [
  { src: "/assets/benny.png",      name: "Benny",  angle: -160, radius: 1.00, size: 128, ring: "#0065F8", drift: -14, react: 16, mobile: [-130, -200] },
  { src: "/assets/zippy-icon.png", name: "Zippy",  angle: -115, radius: 0.80, size: 112, ring: "#00CAFF", drift: 10,  react: 0,  mobile: [-45, -172] },
  { src: "/assets/willow.png",     name: "Willow", angle: -65,  radius: 0.92, size: 122, ring: "#0A7DFF", drift: -8,  react: 12, mobile: [45, -205] },
  { src: "/assets/maya.png",       name: "Maya",   angle: -20,  radius: 1.00, size: 136, ring: "#00CAFF", drift: 12,  react: 18, mobile: [132, -178] },
  { src: "/assets/robo.png",       name: "Robo",   angle: 25,   radius: 0.85, size: 108, ring: "#0065F8", drift: -10, react: 11, mobile: [120, 145] },
  { src: "/assets/cheezy.jpeg",    name: "Cheezy", angle: 70,   radius: 0.95, size: 128, ring: "#0A7DFF", drift: 8,   react: 15, mobile: [55, 262] },
  { src: "/assets/beary.jpeg",     name: "Beary",  angle: 120,  radius: 0.82, size: 114, ring: "#00CAFF", drift: -12, react: 13, mobile: [-55, 255] },
  { src: "/assets/pip.png",        name: "Pip",    angle: 165,  radius: 0.98, size: 122, ring: "#0065F8", drift: 14,  react: 17, mobile: [-120, 150] },
];

/* phones: rows never come closer to the section center than this, so a
   shorter screen squeezes the outer rows in, not the rows onto the title */
const WORLD_MOBILE_CLEAR = 150;

/* where a character settles once flown out, relative to the section center */
const worldTarget = (cfg, isMobile) => {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 844;
  if (isMobile) {
    const [mx, my] = cfg.mobile;
    const sy = Math.min(1, Math.max(620, vh) / 844);
    const beyond = Math.max(0, Math.abs(my) - WORLD_MOBILE_CLEAR);
    return {
      x: mx * Math.min(1, vw / 390),
      y: Math.sign(my) * (Math.min(Math.abs(my), WORLD_MOBILE_CLEAR) + beyond * sy),
    };
  }
  const rad = (cfg.angle * Math.PI) / 180;
  return {
    x: Math.cos(rad) * cfg.radius * Math.min(560, vw * 0.34),
    y: Math.sin(rad) * cfg.radius * 300,
  };
};

/* user gestures that let a video start in iOS Low Power Mode (touchend, not
   touchstart: WebKit only grants playback at the end of a touch) */
const PLAY_GESTURES = ["touchend", "click", "keydown"];

/* a decorative landing clip, run by its position on the page:
   - it shows its poster until it's within a screen of the viewport and only
     then gets its source — on a phone every clip used to start downloading at
     page load (~30MB) and starve the hero, which stalled
   - it plays only while on screen (and `active`), and pauses once scrolled
     away. Left to the browser, a clip a tap had started kept playing
     off screen, so a long scroll piled up a decoder per clip
   - Low Power Mode refuses unprompted playback: the visitor's next tap starts
     whatever is on screen
   NoControlsVideo hides the browser's play button and gives in-app browsers
   `animated` or the poster as an image (TikTok's draws player chrome over any
   <video>); there's no <video> there, so none of the above runs. */
const LandingVideo = ({ src, sources, playbackRate, active = true, ref, ...props }) => {
  const nodeRef = useRef(null);
  const [near, setNear] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const setRefs = useCallback(
    (node) => {
      nodeRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      setOnScreen(true);
      return undefined;
    }
    const nearIo = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          nearIo.disconnect();
        }
      },
      { rootMargin: "100% 0px" }
    );
    const screenIo = new IntersectionObserver((entries) =>
      setOnScreen(entries[entries.length - 1].isIntersecting)
    );
    nearIo.observe(node);
    screenIo.observe(node);
    return () => {
      nearIo.disconnect();
      screenIo.disconnect();
    };
  }, []);

  const shouldPlay = near && onScreen && active;
  useEffect(() => {
    const video = nodeRef.current;
    if (!video) return undefined;
    if (!shouldPlay) {
      video.pause();
      return undefined;
    }
    const start = () => {
      if (!video.paused) return;
      const attempt = video.play();
      // Older engines return undefined rather than a promise
      if (attempt?.catch) attempt.catch(() => {});
    };
    start();
    PLAY_GESTURES.forEach((type) => document.addEventListener(type, start, true));
    return () => PLAY_GESTURES.forEach((type) => document.removeEventListener(type, start, true));
  }, [shouldPlay]);

  /* loading a source resets playbackRate to the default, so set both */
  const applyRate = (e) => {
    e.currentTarget.defaultPlaybackRate = playbackRate;
    e.currentTarget.playbackRate = playbackRate;
  };

  return (
    <NoControlsVideo
      ref={setRefs}
      loop
      preload={near ? "auto" : "none"}
      src={near ? src : undefined}
      onLoadedMetadata={playbackRate ? applyRate : undefined}
      {...props}
    >
      {near && sources?.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
    </NoControlsVideo>
  );
};

/* one circular avatar that pops out of the center and flies outward as the
   section scrolls into view — cubic ease-out, so the first bit of scroll
   moves them the most and the motion tapers off */
const easeOutCubic = (p) => 1 - Math.pow(1 - Math.min(Math.max(p, 0), 1), 3);

const STATIC_ONE = motionValue(1);
const STATIC_ZERO = motionValue(0);

/* footer entrance — a staggered rise-in for each element */
const FOOTER_STAGGER = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } } };
const FOOTER_ITEM = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 240, damping: 22 } },
};
const FOOTER_SOCIALS = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 240, damping: 22, staggerChildren: 0.09, delayChildren: 0.04 } },
};
const FOOTER_SOCIAL_ITEM = {
  hidden: { opacity: 0, scale: 0.3, y: 8 },
  show: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 420, damping: 17 } },
};

const FlyOutAvatar = ({
  progress,
  cfg,
  target,
  innerRef,
  extraOpacity = STATIC_ONE,
  pointerX = STATIC_ZERO,
  pointerY = STATIC_ZERO,
  reactMag = 0,
  sizeScale = 1,
}) => {
  const size = Math.round(cfg.size * sizeScale);
  const { x: targetX, y: targetY } = target;

  const scrollX = useTransform(progress, (p) => targetX * easeOutCubic(p));
  const scrollY = useTransform(progress, (p) => targetY * easeOutCubic(p));
  /* nudge toward the cursor once flown out; magnitude stays tiny */
  const x = useTransform([scrollX, pointerX, progress], ([sx, px, p]) => sx + px * reactMag * p);
  const y = useTransform([scrollY, pointerY, progress], ([sy, py, p]) => sy + py * reactMag * p);
  const scale = useTransform(progress, (p) => 0.2 + 0.8 * easeOutCubic(p / 0.3));
  const baseOpacity = useTransform(progress, (p) => Math.min(1, p / 0.1));
  const opacity = useTransform([baseOpacity, extraOpacity], ([a, b]) => a * b);
  const rotate = useTransform(progress, (p) => cfg.drift * easeOutCubic(p));

  /* a magical little pop + ring burst when the character is tapped */
  const popControls = useAnimationControls();
  const ringControls = useAnimationControls();
  const handleTap = () => {
    popControls.start({
      scale: [1, 1.28, 0.92, 1.12, 1],
      rotate: [0, -10, 9, -4, 0],
      transition: { duration: 0.7, ease: "easeInOut" },
    });
    ringControls.start({
      scale: [0.9, 1.55],
      opacity: [0.8, 0],
      transition: { duration: 0.65, ease: "easeOut" },
    });
  };

  return (
    <motion.div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        marginLeft: -size / 2,
        marginTop: -size / 2,
        x,
        y,
        scale,
        opacity,
        rotate,
        width: size,
        pointerEvents: "none",
      }}
    >
      <motion.div
        whileHover={{ scale: 1.09 }}
        whileTap={{ scale: 0.94 }}
        onTap={handleTap}
        className="relative"
        style={{ pointerEvents: "auto", cursor: "pointer" }}
      >
        <motion.div
          ref={innerRef}
          animate={popControls}
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            overflow: "hidden",
            border: "4px solid #FFFFFF",
            boxShadow: `0px 3px 0px ${cfg.ring}, 0px 16px 30px rgba(11,26,51,0.18)`,
          }}
        >
          <img
            src={cfg.src}
            alt={cfg.name}
            draggable={false}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </motion.div>
        {/* tap ring burst */}
        <motion.div
          aria-hidden
          initial={{ opacity: 0 }}
          animate={ringControls}
          style={{
            position: "absolute",
            inset: -4,
            borderRadius: "50%",
            border: `3px solid ${cfg.ring}`,
            pointerEvents: "none",
          }}
        />
      </motion.div>
      <div className="flex justify-center" style={{ marginTop: 10 }}>
        <span
          style={{
            ...FREDOKA,
            color: "#0B1A33",
            fontWeight: 600,
            fontSize: 14,
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.08)",
            borderRadius: 999,
            padding: "2px 12px",
            boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
            whiteSpace: "nowrap",
          }}
        >
          {cfg.name}
        </span>
      </div>
    </motion.div>
  );
};

const LandingPage = () => {
  const [typingText, setTypingText] = useState("");
  const [wordIdx, setWordIdx] = useState(0);
  const [wordResetKey, setWordResetKey] = useState(0);
  /* shrink the "Your world" circles on small screens */
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_MAX
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_MAX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const inAppBrowser = isEmbeddedWebView();
  const worldRef = useRef(null);
  const drawRef = useRef(null);
  const dockRef = useRef(null);
  const zippyWorldRef = useRef(null);

  /* scroll progress as the "Your world" section enters the viewport —
     fully flown out by the time it's centered on screen */
  const { scrollYProgress: worldProgress } = useScroll({
    target: worldRef,
    offset: ["start 95%", "center center"],
  });
  const worldProgressSmooth = useSpring(worldProgress, { stiffness: 90, damping: 24, mass: 0.6 });

  /* cursor reaction — characters nudge slightly toward the pointer. Raw values
     are normalized -1..1 relative to the section center; springs smooth them */
  const pointerXRaw = useMotionValue(0);
  const pointerYRaw = useMotionValue(0);
  const pointerX = useSpring(pointerXRaw, { stiffness: 120, damping: 20, mass: 0.4 });
  const pointerY = useSpring(pointerYRaw, { stiffness: 120, damping: 20, mass: 0.4 });
  const handleWorldPointer = useCallback((e) => {
    const el = worldRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    pointerXRaw.set(Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / (r.width / 2))));
    pointerYRaw.set(Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / (r.height / 2))));
  }, [pointerXRaw, pointerYRaw]);
  const resetWorldPointer = useCallback(() => {
    pointerXRaw.set(0);
    pointerYRaw.set(0);
  }, [pointerXRaw, pointerYRaw]);

  /* ── Zippy's journey from "Your world" down to the drawn-to-life section ── */
  const { scrollYProgress: drawProgress } = useScroll({
    target: drawRef,
    offset: ["start end", "start 30%"],
  });
  /* very stiff spring so Zippy hugs the scroll instead of trailing it */
  const drawProgressSmooth = useSpring(drawProgress, { stiffness: 420, damping: 40, mass: 0.3 });

  const travelerSize = 112;
  const travelerX = useMotionValue(-200);
  const travelerY = useMotionValue(-200);
  /* visibility switches follow the RAW scroll position (not the spring), so
     fast scrolls / scrollbar drags snap the hand-off instantly with no gap */
  const travelerOpacity = useTransform(drawProgress, [0.01, 0.04, 0.95, 0.99], [0, 1, 1, 0]);
  /* the traveler matches the circle it stands in for — Zippy's smaller
     "Your world" circle at the start (on phones), the dock at the end — plus
     a little pop on landing */
  const travelerSizeScale = useMotionValue(1);
  const travelerScale = useTransform(
    [travelerSizeScale, drawProgressSmooth],
    ([s, p]) => s * (p < 0.9 ? 1 : 1 + 0.8 * (p - 0.9))
  );
  /* the original circle in "Your world" hides once the traveler takes over */
  const zippyHandOff = useTransform(drawProgress, (p) => (p > 0.02 ? 0 : 1));
  /* the docked circle in the next section appears as Zippy lands */
  const dockOpacity = useTransform(drawProgress, [0.93, 0.99], [0, 1]);
  /* the section itself blooms out of the landing point */
  const drawReveal = useTransform(
    drawProgressSmooth,
    (p) => `circle(${(6 + 160 * easeOutCubic(p)).toFixed(1)}% at 50% 0px)`
  );

  /* ── gentle parallax for the Storyverse iPad ── */
  const storyverseRef = useRef(null);
  const { scrollYProgress: storyverseProgress } = useScroll({
    target: storyverseRef,
    offset: ["start end", "end start"],
  });
  const storyverseParallax = useTransform(storyverseProgress, [0, 1], [44, -44]);

  /* ── parallax for the two creation showcases ── */
  const createStoryRef = useRef(null);
  const { scrollYProgress: createStoryProgress } = useScroll({
    target: createStoryRef,
    offset: ["start end", "end start"],
  });
  const createStoryParallax = useTransform(createStoryProgress, [0, 1], [36, -36]);

  const createCharRef = useRef(null);
  const { scrollYProgress: createCharProgress } = useScroll({
    target: createCharRef,
    offset: ["start end", "end start"],
  });
  const createCharParallax = useTransform(createCharProgress, [0, 1], [36, -36]);

  const createBookRef = useRef(null);
  const { scrollYProgress: createBookProgress } = useScroll({
    target: createBookRef,
    offset: ["start end", "end start"],
  });
  const createBookParallax = useTransform(createBookProgress, [0, 1], [36, -36]);

  /* ── the doodle morphs into the living, animated Zippy while the page is
     pinned — the scroll scrubs only the morph. Phones skip the pin (the
     landscape drawing filled a third of the pinned screen, leaving a tall
     blank band above and below it) and scrub as it passes through view ── */
  const morphRef = useRef(null);
  const { scrollYProgress: morphProgress } = useScroll({
    target: morphRef,
    offset: isMobile ? ["start 70%", "end 45%"] : ["start start", "end end"],
  });
  const morph = useSpring(morphProgress, { stiffness: 220, damping: 30, mass: 0.5 });
  const drawingOpacity = useTransform(morph, [0.2, 0.75], [1, 0]);
  const drawingScale = useTransform(morph, [0, 1], [1, 0.9]);
  const drawingRotate = useTransform(morph, [0, 1], [-1.5, -5]);
  const zippyOpacity = useTransform(morph, [0.3, 0.8], [0, 1]);
  const zippyScale = useTransform(morph, [0.2, 1], [0.7, 1]);
  const zippyRotate = useTransform(morph, [0.2, 1], [4, 0]);
  /* the sound toggle only becomes clickable once Zippy is actually visible */
  const zippyBtnPointer = useTransform(morph, (p) => (p > 0.55 ? "auto" : "none"));

  const updateTraveler = useCallback(() => {
    const dock = dockRef.current;
    const zip = zippyWorldRef.current;
    if (!dock || !zip) return;
    const p = drawProgressSmooth.get();
    const z = zip.getBoundingClientRect();
    const d = dock.getBoundingClientRect();
    const startX = z.left + z.width / 2;
    const startY = z.top + z.height / 2;
    const endX = d.left + d.width / 2;
    const endY = d.top + d.height / 2;
    /* ease-out on the path — Zippy runs ahead of the scroll and eases into
       the landing instead of trailing behind */
    const pe = easeOutCubic(p);
    const x = startX + (endX - startX) * pe;
    const y = startY + (endY - startY) * pe;
    travelerX.set(x - travelerSize / 2);
    travelerY.set(y - travelerSize / 2);
    const startScale = z.width / travelerSize;
    const endScale = d.width / travelerSize;
    travelerSizeScale.set(startScale + (endScale - startScale) * pe);
  }, [drawProgressSmooth, travelerX, travelerY, travelerSizeScale]);

  useEffect(() => {
    const unsub = drawProgressSmooth.on("change", updateTraveler);
    return unsub;
  }, [drawProgressSmooth, updateTraveler]);

  /* rotating word under "The world's most personal storyteller" —
     clicking the pill flips it immediately and restarts the timer */
  useEffect(() => {
    const id = setInterval(() => setWordIdx((p) => (p + 1) % ROTATING_WORDS.length), 3000);
    return () => clearInterval(id);
  }, [wordResetKey]);

  const toggleWord = () => {
    setWordIdx((p) => (p + 1) % ROTATING_WORDS.length);
    setWordResetKey((k) => k + 1);
  };

  /* sound toggle for the living-Zippy clip */
  const [zippyMuted, setZippyMuted] = useState(true);
  const zippyVideoRef = useRef(null);
  const toggleZippyAudio = () => {
    setZippyMuted((m) => {
      const next = !m;
      const v = zippyVideoRef.current;
      if (v) {
        v.muted = next;
        if (!next) v.play().catch(() => {});
      }
      return next;
    });
  };

  /* typing effect (kept) */
  useEffect(() => {
    const text = "Literally.";
    let index = 0;
    setTypingText("");
    const tick = () => {
      if (index < text.length) {
        setTypingText(text.slice(0, index + 1));
        index++;
        setTimeout(tick, 100);
      }
    };
    const t = setTimeout(tick, 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="min-h-screen"
      /* overflowX clip (not hidden) — clip doesn't create a scroll container,
         so position:sticky keeps working for any future pinned sections */
      style={{ background: C.cream, ...INTER, overflowX: "clip" }}
    >
      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="lp-hero-h relative overflow-hidden" style={{ background: "#FFF" }}>
        <ProgressiveHeroVideo />

        <div
          className="absolute inset-0 w-full h-full"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,26,51,0.20) 0%, rgba(0,101,248,0.12) 45%, rgba(11,26,51,0.55) 100%)",
          }}
        />

        <div className="lp-hero-h relative z-10 flex items-center justify-center text-center px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: "#FFF",
                fontWeight: 600,
                fontSize: "clamp(2rem, 6vw, 4.25rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: 0,
                /* keeps the white type readable over the clip's bright scenes */
                textShadow: "0 2px 18px rgba(11,26,51,0.35)",
              }}
            >
              Screen time that builds character.
              <br />
              <span
                style={{
                  fontWeight: 800,
                  color: "#FFF",
                }}
              >
                {typingText}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-5 sm:mt-7"
              style={{
                color: "rgba(255,255,255,0.92)",
                fontSize: "clamp(0.95rem, 2vw, 1.2rem)",
                maxWidth: 640,
                lineHeight: 1.55,
                textShadow: "0 1px 12px rgba(11,26,51,0.45)",
              }}
            >
              Personalized cartoons that teach your child.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 sm:mt-10 flex justify-center"
            >
              <motion.a
                href="https://apps.apple.com/us/app/pixley-personalized-cartoons/id6751121342"
                target="_blank"
                rel="noreferrer"
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.96 }}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold focus:outline-none"
                style={{
                  ...FREDOKA,
                  background: `linear-gradient(180deg, ${C.blueLight} 0%, ${C.blue} 100%)`,
                  color: "#FFF",
                  boxShadow: `0px 3px 0px ${C.blueDeep}`,
                  textDecoration: "none",
                  letterSpacing: "0.01em",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                Download on iOS
              </motion.a>
            </motion.div>
          </div>
        </div>

      </section>

      {/* ═══════════════════ YOUR WORLD ═══════════════════ */}
      <section
        ref={worldRef}
        className="relative overflow-hidden"
        style={{ height: "100vh", minHeight: 620, background: C.cream2 }}
      >
        <div
          className="relative flex items-center justify-center"
          style={{ height: "100%" }}
          onMouseMove={handleWorldPointer}
          onMouseLeave={resetWorldPointer}
        >
          {/* soft center glow */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(70vw, 820px)",
              height: "min(70vw, 820px)",
              background: "radial-gradient(closest-side, rgba(0,202,255,0.14), transparent 70%)",
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />

          {/* flying character circles */}
          <div className="absolute inset-0">
            <div style={{ position: "absolute", left: "50%", top: "50%" }}>
              {WORLD_CHARACTERS.map((cfg) => {
                const isZippy = cfg.src.includes("zippy-icon");
                return (
                  <FlyOutAvatar
                    key={cfg.src}
                    progress={worldProgressSmooth}
                    cfg={cfg}
                    sizeScale={isMobile ? 0.6 : 1}
                    target={worldTarget(cfg, isMobile)}
                    innerRef={isZippy ? zippyWorldRef : undefined}
                    extraOpacity={isZippy ? zippyHandOff : undefined}
                    pointerX={isZippy ? undefined : pointerX}
                    pointerY={isZippy ? undefined : pointerY}
                    reactMag={isZippy ? 0 : cfg.react}
                  />
                );
              })}
            </div>
          </div>

          {/* center copy */}
          <div className="relative text-center px-4" style={{ zIndex: 3 }}>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 700,
                fontSize: "clamp(2.75rem, 7vw, 5.5rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Your{" "}
              <span
                style={{
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                world
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mx-auto mt-5"
              style={{
                color: C.slate,
                fontSize: "clamp(1.05rem, 1.8vw, 1.3rem)",
                lineHeight: 1.6,
                maxWidth: 480,
              }}
            >
              Every character your child dreams up lives here.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Zippy in transit — fixed overlay that follows the scroll (and your
          cursor) from "Your world" down to the drawn-to-life section */}
      <motion.div
        aria-hidden
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          x: travelerX,
          y: travelerY,
          width: travelerSize,
          height: travelerSize,
          scale: travelerScale,
          opacity: travelerOpacity,
          borderRadius: "50%",
          overflow: "hidden",
          border: "4px solid #FFFFFF",
          boxShadow: "0px 3px 0px #00CAFF, 0px 16px 30px rgba(11,26,51,0.22)",
          zIndex: 40,
          pointerEvents: "none",
        }}
      >
        <img
          src="/assets/zippy-icon.png"
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </motion.div>

      {/* ═══════════════════ DRAWN TO LIFE ═══════════════════ */}
      <section ref={drawRef} className="relative" style={{ background: C.cream2 }}>
        {/* content blooms out of the point where Zippy lands — clipPath does
            the clipping; no overflow-hidden here or the morph pin would break */}
        <motion.div
          className="relative"
          style={{
            clipPath: drawReveal,
            WebkitClipPath: drawReveal,
            background: "#FFFFFF",
            borderRadius: "clamp(24px, 4vw, 48px)",
          }}
        >
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-14 sm:pt-28 sm:pb-32">
            {/* landing dock — Zippy settles here */}
            <div
              ref={dockRef}
              className="absolute left-1/2 -translate-x-1/2"
              style={{ top: 34, width: travelerSize, height: travelerSize }}
            >
              <motion.div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "4px solid #FFFFFF",
                  boxShadow: "0px 3px 0px #00CAFF, 0px 16px 30px rgba(11,26,51,0.18)",
                  opacity: dockOpacity,
                }}
              >
                <img
                  src="/assets/zippy-icon.png"
                  alt="Zippy, a child-drawn tiger character"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </motion.div>
            </div>

            <div className="mt-16 text-center">
              <motion.div
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  ...FREDOKA,
                  color: C.blue,
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                From a doodle
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="mt-4"
                style={{
                  ...FREDOKA,
                  color: C.navy,
                  fontWeight: 600,
                  fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                  lineHeight: 1.12,
                  letterSpacing: "-0.02em",
                  margin: 0,
                }}
              >
                Drawn by them.{" "}
                <span
                  style={{
                    fontWeight: 700,
                    background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Brought to life.
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 mx-auto"
                style={{
                  color: C.slate,
                  fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)",
                  lineHeight: 1.7,
                  maxWidth: 560,
                }}
              >
                A crayon sketch becomes a talking, walking character — with a
                personality, a voice, and adventures of its own. In about a
                minute.
              </motion.p>

              {/* the doodle morphs into the living Zippy — a tall runway pins
                  the stage so the scroll scrubs only the morph (not on phones;
                  see morphProgress) */}
              <div
                ref={morphRef}
                className="relative -mx-4 sm:-mx-6 lg:-mx-8 mt-6"
                style={isMobile ? undefined : { height: "230vh" }}
              >
                <div
                  className={`${isMobile ? "py-10" : "sticky"} flex items-center justify-center`}
                  style={isMobile ? undefined : { top: 0, height: "100vh" }}
                >
              <motion.div
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.75, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="relative mx-auto w-full px-4 sm:px-6"
                style={{ maxWidth: 880 }}
              >
                {/* stage 1 — the crayon doodle, Pip painting at the corner */}
                <motion.div
                  className="relative"
                  style={{ opacity: drawingOpacity, scale: drawingScale, rotate: drawingRotate }}
                >
                  <img
                    src="/assets/drawing_character.png"
                    alt="A child's crayon drawing of Zippy the tiger in the Pixley drawing screen"
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      borderRadius: "clamp(16px, 2.5vw, 28px)",
                      border: `2px solid ${C.border}`,
                      boxShadow: C.shadow,
                    }}
                  />

                  {/* Pip floating over the iPad, painting the character to life */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ type: "spring", stiffness: 220, damping: 20, delay: 0.45 }}
                    className="absolute"
                    style={{
                      right: "6%",
                      bottom: "20%",
                      width: "clamp(120px, 26%, 230px)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  >
                    <motion.div
                      animate={{ y: [0, -20, 0] }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                    >
                      {/* true alpha transparency — HEVC+alpha for Safari/iOS,
                          VP9+alpha for Chrome/Firefox */}
                      <LandingVideo
                        sources={[
                          { src: "/assets/panda_paint_2.mov", type: 'video/mp4; codecs="hvc1"' },
                          { src: "/assets/panda_paint_2.webm", type: "video/webm" },
                        ]}
                        poster="/assets/panda_paint_2-poster.webp"
                        animated="/assets/panda_paint_2-loop.webp"
                        style={{ width: "100%", height: "auto", aspectRatio: "1 / 1", display: "block" }}
                      />
                    </motion.div>
                  </motion.div>
                </motion.div>

                {/* stage 2 — the living, animated Zippy, bigger than the doodle */}
                <motion.div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "53%",
                    x: "-50%",
                    y: "-45%",
                    width: "min(85%, 650px)",
                    opacity: zippyOpacity,
                    scale: zippyScale,
                    rotate: zippyRotate,
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  <LandingVideo
                    ref={zippyVideoRef}
                    src="/assets/videozippy.mp4"
                    poster="/assets/videozippy-poster.jpg"
                    animated="/assets/videozippy-loop.webp"
                    style={{
                      width: "100%",
                      height: "auto",
                      aspectRatio: "1 / 1",
                      display: "block",
                      borderRadius: "clamp(20px, 3vw, 36px)",
                      border: "4px solid #FFFFFF",
                      boxShadow: "0px 6px 0px rgba(0,202,255,0.35), 0px 30px 60px rgba(11,26,51,0.25)",
                    }}
                  />
                  {/* sound toggle — in-app browsers get the silent animated image */}
                  {!inAppBrowser && (
                  <motion.button
                    type="button"
                    onClick={toggleZippyAudio}
                    aria-label={zippyMuted ? "Unmute Zippy's video" : "Mute Zippy's video"}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="absolute flex items-center justify-center"
                    style={{
                      right: 18,
                      bottom: 18,
                      width: 46,
                      height: 46,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      border: `2px solid ${C.border}`,
                      boxShadow: "0px 3px 0px rgba(11,26,51,0.10), 0px 10px 20px rgba(11,26,51,0.18)",
                      cursor: "pointer",
                      pointerEvents: zippyBtnPointer,
                      color: C.navy,
                    }}
                  >
                    {zippyMuted ? (
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
                        <line x1="23" y1="9" x2="17" y2="15" />
                        <line x1="17" y1="9" x2="23" y2="15" />
                      </svg>
                    ) : (
                      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
                        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                        <path d="M18.8 5.8a9.5 9.5 0 0 1 0 12.4" />
                      </svg>
                    )}
                  </motion.button>
                  )}
                </motion.div>
              </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>


      {/* ═══════════════════ CREATE A CHARACTER ═══════════════════ */}
      <section
        ref={createCharRef}
        className="relative py-16 sm:py-32 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.cream} 0%, ${C.cream2} 100%)` }}
      >
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            bottom: "8%",
            right: -160,
            width: 520,
            height: 520,
            background: "radial-gradient(closest-side, rgba(0,202,255,0.12), transparent 70%)",
            filter: "blur(48px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
          {/* RIGHT: copy */}
          <div className="text-center lg:text-left order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.blue,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Create a character
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4"
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Meet their next{" "}
              <span
                style={{
                  fontWeight: 700,
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                best friend
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 mx-auto lg:mx-0"
              style={{
                color: C.slate,
                fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)",
                lineHeight: 1.7,
                maxWidth: 520,
              }}
            >
              Upload a photo, draw a doodle, or describe them in words —
              Pixley brings the character to life with a look, a voice, and a
              personality all their own.
            </motion.p>
            <div className="mt-7 mx-auto lg:mx-0 w-fit flex flex-col items-start gap-3">
              {[
                "Photo, drawing, or words",
                "A voice and personality included",
                "Theirs to star in every story",
              ].map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.24 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex items-center gap-3"
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: `linear-gradient(180deg, ${C.cyan} 0%, ${C.blue} 100%)`,
                      boxShadow: "0px 2px 0px rgba(7,59,142,0.5)",
                      color: "#FFF",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  <span style={{ ...FREDOKA, color: C.navy, fontWeight: 600, fontSize: 15.5 }}>
                    {line}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* LEFT: video */}
          <motion.div
            className="order-2 lg:order-1 mx-auto w-full"
            style={{ maxWidth: 620, y: createCharParallax }}
          >
            <motion.div
              initial={{ opacity: 0, y: 32, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              >
                <LandingVideo
                  playbackRate={2}
                  src="/assets/final_create_character_cropped.mp4"
                  poster="/assets/final_create_character-poster.jpg"
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "1416 / 1080",
                    display: "block",
                    borderRadius: 24,
                    border: `2px solid ${C.border}`,
                    boxShadow: C.shadow,
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ CREATE A STORY ═══════════════════ */}
      <section
        ref={createStoryRef}
        className="relative py-16 sm:py-32 overflow-hidden"
        style={{ background: C.cream }}
      >
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "12%",
            left: -160,
            width: 520,
            height: 520,
            background: "radial-gradient(closest-side, rgba(0,101,248,0.10), transparent 70%)",
            filter: "blur(48px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
          {/* RIGHT: video */}
          <motion.div
            className="order-2 mx-auto w-full"
            style={{ maxWidth: 620, y: createStoryParallax }}
          >
            <motion.div
              initial={{ opacity: 0, y: 32, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: 1.5 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <LandingVideo
                  playbackRate={2}
                  src="/assets/create_story_video_cropped.mp4"
                  poster="/assets/create_story-poster.jpg"
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "1310 / 1008",
                    display: "block",
                    borderRadius: 24,
                    border: `2px solid ${C.border}`,
                    boxShadow: C.shadow,
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>

          {/* LEFT: copy */}
          <div className="order-1 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.blue,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Create a story
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4"
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              From idea to{" "}
              <span
                style={{
                  fontWeight: 700,
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                episode
              </span>{" "}
              in minutes
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 mx-auto lg:mx-0"
              style={{
                color: C.slate,
                fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)",
                lineHeight: 1.7,
                maxWidth: 520,
              }}
            >
              Pick the characters, choose the lesson, and Pixley writes,
              voices, and animates an original episode starring them.
            </motion.p>
            <div className="mt-7 mx-auto lg:mx-0 w-fit flex flex-col items-start gap-3">
              {[
                "Your characters take the lead",
                "Lessons woven into every plot",
                "Ready in about a minute",
              ].map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.24 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex items-center gap-3"
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: `linear-gradient(180deg, ${C.blueLight} 0%, ${C.blue} 100%)`,
                      boxShadow: "0px 2px 0px rgba(7,59,142,0.5)",
                      color: "#FFF",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  <span style={{ ...FREDOKA, color: C.navy, fontWeight: 600, fontSize: 15.5 }}>
                    {line}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ CREATE A STORYBOOK ═══════════════════ */}
      <section
        ref={createBookRef}
        className="relative py-16 sm:py-32 overflow-hidden"
        style={{ background: C.cream }}
      >
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "14%",
            left: -150,
            width: 500,
            height: 500,
            background: "radial-gradient(closest-side, rgba(0,202,255,0.11), transparent 70%)",
            filter: "blur(48px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
          {/* LEFT: video */}
          <motion.div
            className="order-2 lg:order-1 mx-auto w-full"
            style={{ maxWidth: 620, y: createBookParallax }}
          >
            <motion.div
              initial={{ opacity: 0, y: 32, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
              >
                <LandingVideo
                  playbackRate={2}
                  src="/assets/createstorybook_cropped.mp4"
                  poster="/assets/createstorybook-poster.jpg"
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: "1204 / 914",
                    display: "block",
                    borderRadius: 24,
                    border: `2px solid ${C.border}`,
                    boxShadow: C.shadow,
                  }}
                />
              </motion.div>
            </motion.div>
          </motion.div>

          {/* RIGHT: copy */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.blue,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Create a storybook
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4"
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Bedtime stories,{" "}
              <span
                style={{
                  fontWeight: 700,
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                starring them
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 mx-auto lg:mx-0"
              style={{
                color: C.slate,
                fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)",
                lineHeight: 1.7,
                maxWidth: 520,
              }}
            >
              Turn any idea into an illustrated, narrated storybook — read it
              together, or let Pixley read it aloud.
            </motion.p>
            <div className="mt-7 mx-auto lg:mx-0 w-fit flex flex-col items-start gap-3">
              {[
                "Illustrated page by page",
                "Read-aloud narration included",
                "Perfect for bedtime",
              ].map((line, i) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: 0.24 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-flex items-center gap-3"
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: `linear-gradient(180deg, ${C.blueLight} 0%, ${C.blue} 100%)`,
                      boxShadow: "0px 2px 0px rgba(7,59,142,0.5)",
                      color: "#FFF",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                  <span style={{ ...FREDOKA, color: C.navy, fontWeight: 600, fontSize: 15.5 }}>
                    {line}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ STORYVERSE ═══════════════════ */}
      <section
        ref={storyverseRef}
        className="relative py-16 sm:py-32 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.cream2} 0%, ${C.cream} 100%)` }}
      >
        {/* soft background glows */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "10%",
            right: -160,
            width: 560,
            height: 560,
            background: "radial-gradient(closest-side, rgba(0,202,255,0.14), transparent 70%)",
            filter: "blur(48px)",
          }}
        />
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            bottom: -180,
            left: -140,
            width: 480,
            height: 480,
            background: "radial-gradient(closest-side, rgba(0,101,248,0.09), transparent 70%)",
            filter: "blur(48px)",
          }}
        />
        {/* twinkling sparkles */}
        {[
          { top: "14%", left: "6%", size: 18, delay: 0 },
          { top: "70%", left: "12%", size: 12, delay: 1.1 },
          { top: "20%", right: "8%", size: 14, delay: 0.6 },
        ].map((s, i) => (
          <motion.svg
            key={i}
            aria-hidden
            className="absolute pointer-events-none"
            style={{ top: s.top, left: s.left, right: s.right, color: C.cyan }}
            width={s.size}
            height={s.size}
            viewBox="0 0 24 24"
            fill="currentColor"
            animate={{ opacity: [0.15, 0.7, 0.15], scale: [0.8, 1.15, 0.8], rotate: [0, 20, 0] }}
            transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: s.delay }}
          >
            <path d="M12 2l2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2L12 2z" />
          </motion.svg>
        ))}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
          {/* LEFT: copy */}
          <div className="text-center lg:text-left order-1">
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.blue,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              The Storyverse
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="mt-4"
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              An{" "}
              <span
                style={{
                  fontWeight: 700,
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                endless library
              </span>{" "}
              of stories
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 mx-auto lg:mx-0"
              style={{
                color: C.slate,
                fontSize: "clamp(1.05rem, 1.6vw, 1.3rem)",
                lineHeight: 1.7,
                maxWidth: 520,
              }}
            >
              Every story made in Pixley can join the Storyverse — an
              ever-growing library of shows and storybooks from families
              everywhere. Press play on theirs, or add your own.
            </motion.p>

            {/* feature chips */}
            <div className="mt-7 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              {[
                {
                  label: "Made by families",
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  ),
                },
                {
                  label: "New stories every day",
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 2l2.2 5.8L20 10l-5.8 2.2L12 18l-2.2-5.8L4 10l5.8-2.2L12 2z" />
                    </svg>
                  ),
                },
                {
                  label: "Kid-safe",
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  ),
                },
              ].map((chip, i) => (
                <motion.div
                  key={chip.label}
                  initial={{ opacity: 0, y: 14, scale: 0.92 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ type: "spring", stiffness: 260, damping: 22, delay: 0.24 + i * 0.09 }}
                  className="inline-flex items-center gap-2"
                  style={{
                    background: "#FFFFFF",
                    border: `2px solid ${C.border}`,
                    borderRadius: 999,
                    padding: "7px 14px",
                    color: C.blue,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                  }}
                >
                  {chip.icon}
                  <span style={{ ...FREDOKA, color: C.navy, fontWeight: 600, fontSize: 13 }}>
                    {chip.label}
                  </span>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8"
            >
              <Link
                to="/storyverse"
                className="inline-flex items-center gap-2 transition-transform duration-150 hover:scale-[1.03] active:scale-95"
                style={{
                  ...FREDOKA,
                  background: `linear-gradient(180deg, ${C.blueLight} 0%, ${C.blue} 100%)`,
                  color: "#FFF",
                  fontWeight: 800,
                  fontSize: 14,
                  padding: "14px 26px",
                  borderRadius: 999,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  textDecoration: "none",
                  boxShadow: `0px 4px 0px ${C.blueDeep}, 0px 14px 28px rgba(0,101,248,0.22)`,
                }}
              >
                Explore the Storyverse
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
            </motion.div>
          </div>

          {/* RIGHT: Storyverse on an iPad — parallax drift + idle hover */}
          <motion.div
            className="order-2 mx-auto w-full"
            style={{ maxWidth: 640, y: storyverseParallax }}
          >
            <motion.div
              initial={{ opacity: 0, y: 32, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: 1.5 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.img
                src="/assets/landscape-storyverse.png"
                alt="The Pixley Storyverse library on an iPad"
                animate={{ y: [0, -9, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  filter: "drop-shadow(0px 26px 44px rgba(11,26,51,0.22))",
                }}
              />

            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ STORYTELLER ═══════════════════ */}
      <section
        className="relative py-16 sm:py-32 lg:py-36 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.cream} 0%, ${C.cream2} 100%)` }}
      >
        {/* soft background glows */}
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: -140,
            left: "50%",
            transform: "translateX(-50%)",
            width: 760,
            height: 520,
            background: "radial-gradient(closest-side, rgba(0,202,255,0.16), transparent 70%)",
            filter: "blur(48px)",
          }}
        />
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "8%",
            right: -120,
            width: 520,
            height: 520,
            background: "radial-gradient(closest-side, rgba(0,101,248,0.10), transparent 70%)",
            filter: "blur(48px)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16">
          {/* LEFT: copy */}
          <div className="text-center lg:text-left order-1">
            <motion.h2
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              style={{
                ...FREDOKA,
                color: C.navy,
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.6vw, 3.5rem)",
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              The world's most{" "}
              <span
                style={{
                  fontWeight: 700,
                  background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                personal
              </span>{" "}
              storytelling app
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 sm:mt-9"
            >
              <p
                style={{
                  color: C.slate,
                  fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)",
                  lineHeight: 1.75,
                  margin: 0,
                  maxWidth: 560,
                }}
                className="mx-auto lg:mx-0"
              >
                {/* "Personalized [pill]" never splits; on a phone the rest
                    flows onto the lines below it instead of stranding "grow" */}
                <span style={{ whiteSpace: "nowrap" }}>
                  Personalized{" "}
                  <span
                    style={{
                      display: "inline-grid",
                      verticalAlign: "middle",
                      margin: "0 6px 14px",
                    }}
                  >
                    {ROTATING_WORDS.map((word, i) => {
                      const active = wordIdx === i;
                      return (
                        <motion.span
                          key={word}
                          initial={false}
                          onClick={toggleWord}
                          whileHover={active ? { scale: 1.06, rotate: -4 } : undefined}
                          whileTap={active ? { scale: 0.94 } : undefined}
                          animate={{
                            opacity: active ? 1 : 0,
                            y: active ? 0 : 18,
                            scale: active ? 1 : 0.82,
                            rotate: -6,
                          }}
                          transition={{
                            duration: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                            opacity: { duration: 0.25, delay: active ? 0.15 : 0 },
                          }}
                          aria-hidden={!active}
                          title="Click to flip"
                          style={{
                            gridArea: "1 / 1",
                            justifySelf: "stretch",
                            textAlign: "center",
                            ...FREDOKA,
                            fontWeight: 700,
                            fontSize: "1.5em",
                            lineHeight: 1.25,
                            color: C.blue,
                            background: "#FFFFFF",
                            border: "2.5px solid rgba(0,202,255,0.55)",
                            borderRadius: 14,
                            padding: "2px 16px",
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            boxShadow:
                              "0px 4px 0px rgba(0,202,255,0.30), 0px 12px 24px rgba(0,101,248,0.12)",
                            cursor: active ? "pointer" : "default",
                            pointerEvents: active ? "auto" : "none",
                            userSelect: "none",
                          }}
                        >
                          {word}
                        </motion.span>
                      );
                    })}
                  </span>
                </span>{" "}
                <span style={{ whiteSpace: "nowrap" }}>that grow</span>
                <br className="hidden sm:inline" />{" "}
                <strong style={{ color: C.navy, fontWeight: 600 }}>creativity</strong>,{" "}
                <strong style={{ color: C.navy, fontWeight: 600 }}>literacy</strong>, and{" "}
                <strong style={{ color: C.navy, fontWeight: 600 }}>emotional intelligence</strong>.
              </p>
            </motion.div>

            {/* made-with-your-character badge */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 inline-flex items-center"
              style={{
                gap: 12,
                background: "#FFFFFF",
                border: `2px solid ${C.border}`,
                borderRadius: 999,
                padding: "8px 10px 8px 20px",
                boxShadow: "0px 3px 0px rgba(11,26,51,0.06), 0px 10px 22px rgba(11,26,51,0.08)",
              }}
            >
              <span
                style={{
                  ...FREDOKA,
                  color: C.navy,
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "0.01em",
                  whiteSpace: "nowrap",
                }}
              >
                Made with{" "}
                <span
                  style={{
                    fontWeight: 700,
                    background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  your own
                </span>{" "}
                character
              </span>
              <motion.span
                whileHover={{ scale: 1.12, rotate: 6 }}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  display: "block",
                  border: "3px solid #FFFFFF",
                  boxShadow: "0px 2px 0px #00CAFF, 0px 8px 16px rgba(11,26,51,0.18)",
                }}
              >
                <img
                  src="/assets/zippy-icon.png"
                  alt="Zippy, a character made in Pixley"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </motion.span>
            </motion.div>
          </div>

          {/* RIGHT: one clip at a time — swaps in sync with the rotating word */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative order-2 mx-auto w-full"
            style={{ height: "clamp(380px, 42vw, 640px)", maxWidth: 620 }}
          >
            {/* soft stage glow */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: "8% 12%",
                background: "radial-gradient(closest-side, rgba(0,101,248,0.10), transparent 72%)",
                filter: "blur(32px)",
                pointerEvents: "none",
              }}
            />
            {/* ground shadow */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                bottom: "2%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "52%",
                height: 26,
                background: "radial-gradient(closest-side, rgba(11,26,51,0.16), transparent)",
                filter: "blur(10px)",
                pointerEvents: "none",
              }}
            />
            {STORYTELLER_MEDIA.map((m) => {
              const active = ROTATING_WORDS[wordIdx] === m.word;
              return (
                <motion.div
                  key={m.word}
                  initial={false}
                  animate={{
                    opacity: active ? 1 : 0,
                    scale: active ? 1 : 0.93,
                    rotate: active ? m.tilt : 0,
                    y: active ? 0 : 14,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                    opacity: { duration: 0.35, delay: active ? 0.12 : 0 },
                  }}
                  aria-hidden={!active}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                    zIndex: active ? 2 : 1,
                    /* a filter over a playing video is redrawn every frame —
                       too heavy for a phone, which keeps the ground shadow */
                    filter: isMobile ? "none" : "drop-shadow(0px 22px 34px rgba(11,26,51,0.18))",
                  }}
                >
                  <LandingVideo
                    /* only the clip matching the rotating word plays */
                    active={active}
                    sources={[
                      { src: m.mov, type: 'video/mp4; codecs="hvc1"' },
                      { src: m.webm, type: "video/webm" },
                    ]}
                    poster={m.poster}
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                      /* the source clips have square corners baked in — round
                         them off so only the tablet shell shows */
                      borderRadius: "7.5% / 5.7%",
                    }}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="relative overflow-hidden">
        {/* ── video banner: logo, tagline & download over the meadow scene ── */}
        <div className="relative overflow-hidden">
          {/* animated meadow scene */}
          <LandingVideo
            src="/assets/footer_vid_cropped.mp4"
            poster="/assets/footer_vid-poster.jpg"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center 55%" }}
            aria-hidden
          />
          {/* very light Pixley-blue wash */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(0,101,248,0.05) 0%, rgba(0,101,248,0.09) 50%, rgba(0,101,248,0.12) 78%, rgba(0,101,248,0) 100%)",
            }}
          />
          {/* top blend — emerges from the previous section's cream */}
          <div
            aria-hidden
            className="absolute inset-x-0 top-0"
            style={{
              height: "24%",
              background:
                "linear-gradient(180deg, #EAF3FF 0%, rgba(234,243,255,0.85) 30%, rgba(234,243,255,0.4) 62%, transparent 100%)",
            }}
          />
          {/* bottom blend — melts into the meadow-green footer bar below */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0"
            style={{
              height: "34%",
              background:
                "linear-gradient(180deg, transparent 0%, rgba(205,226,166,0.35) 46%, rgba(205,226,166,0.82) 80%, #CDE2A6 100%)",
            }}
          />

          {/* content */}
          <div className="relative">
            <div style={{ height: "clamp(20px, 2.5vw, 40px)" }} />
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={FOOTER_STAGGER}
              className="max-w-3xl mx-auto px-4 sm:px-6 text-center"
            >
              {/* Pixley logo — transparent, blended into the top of the scene */}
              <motion.div variants={FOOTER_ITEM} className="flex justify-center">
                <img
                  src="/assets/pixley-logo-full.png"
                  alt="Pixley"
                  style={{
                    height: isMobile ? 64 : 96,
                    objectFit: "contain",
                    display: "block",
                    filter: "drop-shadow(0px 8px 18px rgba(11,26,51,0.28))",
                  }}
                />
              </motion.div>

              <motion.p
                variants={FOOTER_ITEM}
                className="mx-auto mt-4 sm:mt-6"
                style={{
                  ...FREDOKA,
                  color: C.navy,
                  fontWeight: 600,
                  fontSize: "clamp(1.05rem, 2.2vw, 1.6rem)",
                  lineHeight: 1.35,
                  letterSpacing: "-0.01em",
                  maxWidth: 520,
                  textShadow: "0 1px 16px rgba(245,250,255,0.85)",
                }}
              >
                Where every kid becomes the{" "}
                <span
                  style={{
                    fontWeight: 700,
                    background: `linear-gradient(92deg, ${C.blue} 0%, ${C.cyan} 110%)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  star
                </span>{" "}
                of their own story.
              </motion.p>

              {/* download CTA */}
              <motion.div variants={FOOTER_ITEM} className="flex justify-center" style={{ marginTop: isMobile ? 22 : 40 }}>
                <motion.a
                  href="https://apps.apple.com/us/app/pixley-personalized-cartoons/id6751121342"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  className="inline-flex items-center gap-2 rounded-full"
                  style={{
                    ...FREDOKA,
                    background: `linear-gradient(180deg, ${C.blueLight} 0%, ${C.blue} 100%)`,
                    color: "#FFF",
                    fontWeight: 800,
                    fontSize: 15,
                    padding: "14px 30px",
                    boxShadow: `0px 4px 0px ${C.blueDeep}, 0px 14px 28px rgba(0,101,248,0.28)`,
                    textDecoration: "none",
                    letterSpacing: "0.01em",
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  Download on iOS
                </motion.a>
              </motion.div>
            </motion.div>

            {/* extra scene space so the meadow extends farther down (less
                on phones, where the banner was most of a screen) */}
            <div style={{ height: isMobile ? 76 : "clamp(130px, 17vw, 240px)" }} />
          </div>
        </div>

        {/* ── designed footer bar — meadow-green, blends from the video bottom ── */}
        <div style={{ background: "linear-gradient(180deg, #CDE2A6 0%, #BBD68F 100%)" }}>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={FOOTER_STAGGER}
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-6 sm:pt-9 sm:pb-9"
          >
            {/* top row — links on the left, socials on the right */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
              {/* nav links */}
              <motion.div variants={FOOTER_ITEM} className="flex flex-wrap items-center justify-center md:justify-start gap-x-1 gap-y-2">
                {[
                  { label: "Storyverse", to: "/storyverse" },
                  { label: "Blog", to: "/blog" },
                  { label: "Privacy", to: "/privacy-policy" },
                  { label: "Download", to: "/download" },
                ].map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    /* narrower padding on phones keeps all four on one row */
                    className="rounded-full transition-all duration-150 px-2.5 py-2 sm:px-4"
                    style={{
                      ...FREDOKA,
                      color: C.navy,
                      fontWeight: 600,
                      fontSize: 15,
                      textDecoration: "none",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.55)"; e.currentTarget.style.color = C.blue; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.navy; }}
                  >
                    {l.label}
                  </Link>
                ))}
              </motion.div>

              {/* socials */}
              <motion.div variants={FOOTER_SOCIALS} className="flex items-center justify-center md:justify-end gap-3">
                {[
                  { src: "/assets/instagram.png", label: "Instagram", href: "https://www.instagram.com/pixleyai" },
                  { src: "/assets/tiktok.png", label: "TikTok", href: "https://www.tiktok.com/@pixley.ai" },
                  {
                    label: "YouTube",
                    href: "https://www.youtube.com/@PixleyAI",
                    node: (
                      <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden>
                        <path fill="#FF0000" d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8z" />
                        <path fill="#FFFFFF" d="M9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
                      </svg>
                    ),
                  },
                  { src: "/assets/linkedin.png", label: "LinkedIn", href: "https://www.linkedin.com/company/pixley-ai/" },
                ].map((s) => (
                  <motion.a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={s.label}
                    variants={FOOTER_SOCIAL_ITEM}
                    whileHover={{ scale: 1.12, y: -2 }}
                    whileTap={{ scale: 0.92 }}
                    className="flex items-center justify-center"
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      background: "#FFFFFF",
                      border: "1.5px solid rgba(11,26,51,0.06)",
                      boxShadow: "0px 3px 10px rgba(11,26,51,0.12)",
                    }}
                  >
                    {s.node || <img src={s.src} alt="" style={{ width: 19, height: 19, objectFit: "contain" }} />}
                  </motion.a>
                ))}
              </motion.div>
            </div>

            {/* divider */}
            <motion.div variants={FOOTER_ITEM} className="mt-5 sm:mt-8" style={{ height: 1, background: "rgba(11,26,51,0.14)" }} />

            {/* bottom row — copyright left, status right */}
            <motion.div
              variants={FOOTER_ITEM}
              className="flex flex-col sm:flex-row items-center justify-between gap-1.5 sm:gap-3 mt-4 sm:mt-6"
              style={{ ...INTER, color: C.navy, fontSize: 13, fontWeight: 500 }}
            >
              <span>© 2026 Pixley AI · Made with 💙 in San Francisco</span>
              <span className="inline-flex items-center gap-1.5">
                <motion.span
                  animate={{ opacity: [1, 0.35, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  style={{ width: 7, height: 7, borderRadius: "50%", background: "#0B7A3B", display: "inline-block" }}
                />
                All systems operational
              </span>
            </motion.div>
          </motion.div>
        </div>
      </footer>

      <style>{`
        @keyframes blink {
          50% { opacity: 0; }
        }
        /* the hero fills the screen as first shown: on iOS 100vh is the
           height with the toolbars hidden, which pushed the CTA low */
        .lp-hero-h { height: 100vh; height: 100svh; }
      `}</style>
    </div>
  );
};

export default LandingPage;
